import * as React from 'react'
import { SERVICE_LIST_TYPE } from '../../../state/constants/index'
import { IServiceArchive, IServiceObject, IServiceStateFilters } from '../../../types/index'
import { IServiceProgramObject } from '../../../types/service-program'
import { Link, RouteComponentProps, withRouter } from 'react-router-dom'
import ReactTablePagination from 'src/components/ui/ReactTablePagination'
import { MoonLoader } from 'react-spinners'
import classNames from 'classnames'
import { Column, useTable, useSortBy, usePagination, useRowState } from 'react-table'
import ContextMenu from 'src/components/ui/ContextMenu'
import EditIcon from 'src/components/icons/ui/Edit'
import ArchiveIcon from 'src/components/icons/ui/Archive'
import RestoreArchiveIcon from 'src/components/icons/ui/RestoreArchive'
import { useRef } from 'react'
import ImageView from '../../ui/ImageView'
import * as constants from 'src/state/constants/index'
import UIMessages from 'src/utils/ui-messages'
import { viewOptionTypes } from 'src/constants/service'

interface IServiceListProps extends RouteComponentProps<any> {
  type: SERVICE_LIST_TYPE.ACTIVE | SERVICE_LIST_TYPE.ARCHIVED
  items: IServiceObject[]
  servicePrograms: { [id: string]: IServiceProgramObject }
  orgId: string
  columns: string[]
  fetching: boolean
  fetchingError: string
  isArchive: IServiceArchive
  isRestore: IServiceArchive
  filters: IServiceStateFilters
  serviceListTotal: number
  onComponentMount: (params: IServiceStateFilters) => void
  showArchiveModal: (serviceId: string) => void
  showRestoreModal: (serviceId: string) => void
  updateServiceFilters: (params: IServiceStateFilters) => Promise<any>
  fetchServicePrograms: () => void
}

interface IProps<T extends object> {
  columns: Array<Column<T>>
  data: T[]
  loading: boolean
  pageCount: number
  fetchData: (params: { pageIndex: number; pageSize: number }) => void
  updateFilters: (params: IServiceStateFilters) => Promise<any>
  // filters: ITripFilters
  history: any
  pageSize: number
  setPageSize: (size: number) => any
  pageIndex: number
  setPageIndex: (size: number) => any
}

const PAGE_SIZE = 10

function Table<T extends object>({
  columns,
  data,
  fetchData,
  updateFilters,
  loading,
  pageCount: controlledPageCount,
  pageSize,
  setPageSize,
  pageIndex,
  setPageIndex,
  history
}: IProps<T>) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, page, prepareRow, gotoPage, state } = useTable<T>(
    {
      columns,
      data,
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useSortBy,
    usePagination,
    useRowState
  )
  const { sortBy } = state
  const [canPreviousPage, setCanPreviousPage] = React.useState(false)
  const [canNextPage, setCanNextPage] = React.useState(false)

  const mounted = useRef<boolean>()
  React.useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
    } else {
      fetchData({ pageSize, pageIndex })
    }
  }, [pageIndex, pageSize])

  React.useEffect(() => {
    if (pageIndex > 0) {
      setCanPreviousPage(true)
    } else {
      setCanPreviousPage(false)
    }

    if (pageIndex === controlledPageCount - 1) {
      setCanNextPage(false)
    } else {
      setCanNextPage(true)
    }
  }, [pageIndex, pageSize, controlledPageCount])

  React.useEffect(() => {
    let sort = ''
    let orderBy = ''
    sortBy.map(s => {
      s.desc ? (sort = 'DESC') : (sort = 'ASC')
      orderBy = s.id
    })
    if (sort && orderBy) {
      updateFilters({ sort, orderBy })
    }
  }, [sortBy])

  React.useEffect(() => {
    setPageIndex(0)
  }, [pageSize])

  const nextPage = () => {
    setPageIndex(pageIndex + 1)
  }

  const previousPage = () => {
    setPageIndex(pageIndex - 1)
  }

  return (
    <React.Fragment>
      {loading ? (
        <div className='flex flex-column justify-center items-center' style={{ width: '90%', height: '40vh' }}>
          <MoonLoader sizeUnit={'px'} size={40} color={'#405264'} loading={true} />
          <span className='pt1'>Loading...</span>
        </div>
      ) : (
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    className={classNames('', {
                      sortable: column.canSort,
                      sortedAsc: column.isSortedDesc === false,
                      sortedDesc: column.isSortedDesc === true
                    })}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map(
              (row, i) =>
                +prepareRow(row) || (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell, index) => {
                      return (
                        <td {...cell.getCellProps()} key={`1-${index}`}>
                          {cell.render('Cell')}
                        </td>
                      )
                    })}
                  </tr>
                )
            )}
          </tbody>
        </table>
      )}
      <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
        rowCount={rows.length}
      />
    </React.Fragment>
  )
}

function ServiceList(props: IServiceListProps) {
  const [loading, setLoading] = React.useState(false)
  const [pageCount, setPageCount] = React.useState(0)
  const [pageIndex, setPageIndex] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(PAGE_SIZE)
  const [serviceProgramsLoading, setServiceProgramsLoading] = React.useState(true)
  const [columns, setColumns] = React.useState<Column[]>([])

  React.useEffect(() => {
    props.fetchServicePrograms()
  }, [])

  const mounted = useRef<boolean>()
  React.useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
    } else if (!serviceProgramsLoading && mounted.current) {
      fetchData({ pageIndex, pageSize })
    }
  }, [props.filters, props.orgId, props.isArchive, props.isRestore, serviceProgramsLoading])

  React.useEffect(() => {
    if (!props.fetching) {
      setServiceProgramsLoading(false)
    }
  }, [props.fetching])

  React.useEffect(() => {
    if (props.serviceListTotal !== undefined) {
      setPageCount(Math.ceil(props.serviceListTotal / pageSize))
    }
  }, [props.serviceListTotal, pageSize])

  const fetchData = React.useCallback(async ({ pageSize, pageIndex }) => {
    setLoading(true)
    if (props.filters.organizationId && props.type) {
      try {
        await props.onComponentMount({ ...props.filters, type: props.type, take: pageSize, skip: pageIndex * pageSize })
        setPageSize(pageSize)
        setLoading(false)
      } catch (error) {
        setLoading(false)
      }
    }
  }, [props.filters, props.orgId, props.isArchive, props.isRestore, props.servicePrograms])

  React.useEffect(() => {
    if (!serviceProgramsLoading && props.servicePrograms) {
      const newColumns: Column[] = [
        {
          Header: constants.SERVICE_COLUMN.ICON,
          id: 'icon',
          disableSortBy: true,
          accessor: (service: IServiceObject) => {
            return (
              <Link to={`/services/${service.id}`}>
                <ImageView name={service.name} entityId={service.id} isRounded={true} />
              </Link>
            )
          }
        },
        {
          Header: constants.SERVICE_COLUMN.NAME,
          id: 'services.name',
          accessor: (service: IServiceObject) => <Link to={`/services/${service.id}`}>{service.name}</Link>
        },
        {
          Header: constants.SERVICE_COLUMN.INTERNALNAME,
          id: 'services.internalName',
          accessor: (service: IServiceObject) => {
            return (
              <Link to={`/services/${service.id}`}>
                <span> {service.internalName ? service.internalName : 'None'} </span>
              </Link>
            )
          }
        },
        {
          Header: constants.SERVICE_COLUMN.MEMBERSHIP_REQUIRED,
          id: 'services.isMembershipRequired',
          accessor: (service: IServiceObject) => (
            <Link to={`/services/${service.id}`}>{service.isMembershipRequired ? 'Yes' : 'No'}</Link>
          )
        },
        {
          Header: constants.SERVICE_COLUMN.VIEW_OPTIONS,
          id: 'services.viewOption',
          accessor: (service: IServiceObject) => (
            <Link to={`/services/${service.id}`}>
              {service.viewOption === viewOptionTypes.public
                ? UIMessages.service.publicService
                : UIMessages.service.privateService}
            </Link>
          )
        },
        {
          Header: constants.SERVICE_COLUMN.TRIP_LIMIT,
          id: 'services.tripLimit',
          accessor: (service: IServiceObject) => (
            <Link to={`/services/${service.id}`}>
              {service.tripLimit ? `${service.tripLimit} trips per ${service.tripLimitDuration}` : 'No Limit'}
            </Link>
          )
        },
        {
          Header: constants.SERVICE_COLUMN.PROGRAM,
          id: 'serviceProgram',
          disableSortBy: true,
          accessor: (service: IServiceObject) => {
            let serviceName = 'None'
            if (service.serviceProgram) {
              if (typeof service.serviceProgram === 'string') {
                const serviceProgramData = service.serviceProgram
                const servicePrograms = props.servicePrograms[serviceProgramData]
                serviceName = servicePrograms ? servicePrograms.name : 'None'
              } else {
                serviceName = extractServiceProgramName(service.serviceProgram)
              }
            }
            return (
              <Link to={`/services/${service.id}`}>
                <span> {serviceName} </span>
              </Link>
            )
          }
        },
        {
          Header: '',
          id: 'context-menu',
          Cell: ({ value }) => <div className=''>{value}</div>,
          disableSortBy: true,
          accessor: (product: IServiceObject) => {
            return (
              <ServiceItemMenu
                serviceId={product.id}
                type={props.type}
                showArchiveModal={props.showArchiveModal}
                showRestoreModal={props.showRestoreModal}
              />
            )
          }
        }
      ]
      setColumns(newColumns)
    }
  }, [props.servicePrograms, serviceProgramsLoading])

  function extractServiceProgramName(obj: any) {
    for (const key in obj) {
      if (typeof obj[key] === 'string' && /^[A-Z]/.test(obj[key])) {
        return obj[key]
      }
    }
    return 'None'
  }

  const [updateColumn, setUpdateColumns] = React.useState(columns)

  const ServiceItemMenu = (props: {
    serviceId: string
    type: string
    showArchiveModal: (serviceId: string) => void
    showRestoreModal: (serviceId: string) => void
  }) => {
    const { serviceId, type, showArchiveModal, showRestoreModal } = props

    const handleArchiveClick = () => {
      showArchiveModal(serviceId)
    }

    const handleRestoreClick = () => {
      showRestoreModal(serviceId)
    }
    return (
      <ContextMenu>
        {type === SERVICE_LIST_TYPE.ACTIVE ? (
          <React.Fragment>
            <div className='MenuItem flex items-center cursor--pointer p02'>
              <Link to={`/services/${serviceId}/edit`}>
                <EditIcon className='icon--small svg-stroke-grey3 mr1' />
                <span className='font-dark-grey h5'>Edit</span>
              </Link>
            </div>
            <div onClick={handleArchiveClick} className='MenuItem flex items-center cursor--pointer p02'>
              <ArchiveIcon className='icon--small svg-stroke-grey3 mr1' />
              <span className='font-dark-grey h5'>Move to Archive</span>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div onClick={handleRestoreClick} className='MenuItem flex items-center cursor--pointer p02'>
              <RestoreArchiveIcon className='icon--small svg-stroke-grey3 mr1' />
              <span className='font-dark-grey h5'>Restore</span>
            </div>
          </React.Fragment>
        )}
      </ContextMenu>
    )
  }

  React.useEffect(() => {
    let columnData: any = columns
    setUpdateColumns(columns)
    columnData.filter((col: any) => {
      if (col.Header && !props.columns.includes(col.Header)) {
        columnData = columnData.filter((column: any) => column.Header !== col.Header)
        setUpdateColumns(columnData)
      }
    })
  }, [props.columns])

  return (
    <div className='relative px2 py2'>
      <div className='react-table'>
        {columns === null ? (
          <h1>Loading service programs...</h1>
        ) : !serviceProgramsLoading ? (
          props.items ? (
            <Table
              columns={columns}
              data={props.items}
              fetchData={fetchData}
              // filters={props.filters}
              updateFilters={props.updateServiceFilters}
              loading={loading}
              pageCount={pageCount}
              history={props.history}
              pageSize={pageSize}
              setPageSize={setPageSize}
              pageIndex={pageIndex}
              setPageIndex={setPageIndex}
            />
          ) : (
            <h1>No service available</h1>
          )
        ) : (
          <h1>Loading services...</h1>
        )}
      </div>
    </div>
  )
}

export default withRouter(ServiceList)



// WEBPACK FOOTER //
// ./src/components/services/list/ServiceList.tsx